// Moving a meal to another day (redesign slice 5d, the desktop week's drag),
// and the row changes any plan edit needs saved. Pure functions, tested in
// tests/plan-moves.test.mjs.
//
// A move takes a whole meal (every dish in it) to another day or meal; if
// that meal is already planned, the two swap. Leftovers links move with the
// dishes: a link to a moved dish follows it, and a moved leftovers slot
// keeps its source. A link left pointing at a meal that is no longer
// earlier than its leftovers is dropped (the slot stays as "Something
// older"), as the picker would never have offered it.
import { DINING_OUT, MEAL_SLOTS } from "./constants";
import { normalizeDishes } from "./planUtils";
import { isCooked, linkTo, mealDate, mealKey, type LeftoverSource, type MealRef } from "./planScaling";
import type { Day, DishType, MealSlot, PlansByWeek, WeekPlan } from "./types";

export interface MealPlace {
  day: Day;
  slot: MealSlot;
}

const samePlace = (a: MealPlace, b: MealPlace) => a.day === b.day && a.slot === b.slot;
// LeftoverSource / MealRef use `mealSlot`; places use `slot`.
const slotPlace = (ref: { day: Day; mealSlot: MealSlot }): MealPlace => ({ day: ref.day, slot: ref.mealSlot });
const dishTypesAt = (week: WeekPlan, place: MealPlace) => Object.keys(normalizeDishes(week.days?.[place.day]?.[place.slot]) as Record<string, string>);

// Whether `source` comes before the leftovers at `target`: an earlier day,
// or an earlier meal the same day.
export function isEarlier(source: MealRef, target: MealRef): boolean {
  const s = mealDate(source.weekStart, source.day).getTime();
  const t = mealDate(target.weekStart, target.day).getTime();
  return s < t || (s === t && MEAL_SLOTS.indexOf(source.mealSlot) < MEAL_SLOTS.indexOf(target.mealSlot));
}

// One meal's dishes, leftovers marks, links and cooked marks, keyed by dish type.
interface MealCells {
  dishes: Record<string, string>;
  leftovers: Record<string, boolean>;
  sources: Record<string, LeftoverSource>;
  cooked: Record<string, string>;
}

function takeMeal(week: WeekPlan, place: MealPlace): MealCells {
  const dishes = { ...(normalizeDishes(week.days?.[place.day]?.[place.slot]) as Record<string, string>) };
  const leftovers: Record<string, boolean> = {};
  const sources: Record<string, LeftoverSource> = {};
  const cooked: Record<string, string> = {};
  for (const dt of Object.keys(dishes)) {
    const key = mealKey(place.day, place.slot, dt);
    if (week.leftovers?.[key]) leftovers[dt] = true;
    if (week.leftoverSources?.[key]) sources[dt] = week.leftoverSources[key];
    if (week.cooked?.[key]) cooked[dt] = week.cooked[key];
  }
  return { dishes, leftovers, sources, cooked };
}

const hasMeal = (cells: MealCells) => Object.values(cells.dishes).some(Boolean);

// Move the meal at `from` to `to` in `weekStart`, swapping with whatever is
// there. Returns the plans unchanged when there is nothing to move.
export function moveMeal(plansByWeek: PlansByWeek, weekStart: string, from: MealPlace, to: MealPlace, now: Date): PlansByWeek {
  const week = plansByWeek[weekStart];
  if (!week || samePlace(from, to)) return plansByWeek;
  const a = takeMeal(week, from);
  const b = takeMeal(week, to);
  if (!hasMeal(a)) return plansByWeek;

  // Write each meal's cells at its new place.
  const days = { ...week.days };
  const leftovers = { ...(week.leftovers || {}) };
  const leftoverSources = { ...(week.leftoverSources || {}) };
  const cooked = { ...(week.cooked || {}) };
  for (const [place, cells] of [
    [from, b],
    [to, a],
  ] as const) {
    for (const dt of dishTypesAt(week, place)) {
      delete leftovers[mealKey(place.day, place.slot, dt)];
      delete leftoverSources[mealKey(place.day, place.slot, dt)];
      delete cooked[mealKey(place.day, place.slot, dt)];
    }
    const daySlots = { ...(days[place.day] || {}) };
    if (hasMeal(cells)) daySlots[place.slot] = cells.dishes;
    else delete daySlots[place.slot];
    days[place.day] = daySlots;
    for (const dt of Object.keys(cells.dishes)) {
      if (cells.leftovers[dt]) leftovers[mealKey(place.day, place.slot, dt)] = true;
      if (cells.sources[dt]) leftoverSources[mealKey(place.day, place.slot, dt)] = cells.sources[dt];
      if (cells.cooked[dt]) cooked[mealKey(place.day, place.slot, dt)] = cells.cooked[dt];
    }
  }
  let out: PlansByWeek = { ...plansByWeek, [weekStart]: { ...week, days, leftovers, leftoverSources, cooked } };

  // Links anywhere that pointed at a moved dish follow it, re-dated as of
  // now; then every link must still point at an earlier meal.
  const moved = (src: LeftoverSource): MealPlace | null => {
    if (src.weekStart !== weekStart) return null;
    if (samePlace(slotPlace(src), from)) return to;
    if (samePlace(slotPlace(src), to)) return from;
    return null;
  };
  for (const [ws, wk] of Object.entries(out)) {
    const entries = Object.entries(wk.leftoverSources || {});
    let changed = false;
    const next: Record<string, LeftoverSource> = {};
    for (const [key, src] of entries) {
      const dest = moved(src);
      const destRef = dest && { weekStart, day: dest.day, mealSlot: dest.slot, dishType: src.dishType };
      const link = destRef ? linkTo(destRef, now, isCooked(out, destRef)) : src;
      const [day, slot, dish] = key.split("|") as [Day, MealSlot, DishType];
      const keep = isEarlier(link, { weekStart: ws, day, mealSlot: slot, dishType: dish });
      if (keep) next[key] = link;
      if (dest || !keep) changed = true;
    }
    if (changed) out = { ...out, [ws]: { ...wk, leftoverSources: next } };
  }
  return out;
}

// ------------------------------------------------------------ saving ---

export interface PlacementRow extends MealRef {
  recipeId: string; // "" for an empty row, or DINING_OUT
  isLeftover: boolean;
  source: LeftoverSource | null;
  cookedAt: string | null; // the cooked mark (0058)
}

export interface PlacementChanges {
  upserts: PlacementRow[];
  deletes: MealRef[];
}

function rowsOf(weekStart: string, week: WeekPlan | undefined): Map<string, PlacementRow> {
  const rows = new Map<string, PlacementRow>();
  for (const [day, slots] of Object.entries(week?.days || {})) {
    for (const [slot, raw] of Object.entries(slots || {})) {
      for (const [dishType, recipeId] of Object.entries(normalizeDishes(raw) as Record<string, string>)) {
        const key = mealKey(day, slot, dishType);
        const isLeftover = !!week?.leftovers?.[key] && !!recipeId && recipeId !== DINING_OUT;
        const cookedAt = !isLeftover && recipeId && recipeId !== DINING_OUT ? (week?.cooked?.[key] ?? null) : null;
        rows.set(key, { weekStart, day: day as Day, mealSlot: slot as MealSlot, dishType: dishType as DishType, recipeId, isLeftover, source: isLeftover ? (week?.leftoverSources?.[key] ?? null) : null, cookedAt });
      }
    }
  }
  return rows;
}

const sameSource = (a: LeftoverSource | null, b: LeftoverSource | null) =>
  a === b || (!!a && !!b && a.weekStart === b.weekStart && a.day === b.day && a.mealSlot === b.mealSlot && a.dishType === b.dishType && a.scalesSource === b.scalesSource);

// The week_plan_meals rows that differ between two versions of the plan:
// rows to write (new or changed) and rows to delete. Weeks whose object is
// unchanged are skipped.
export function placementChanges(before: PlansByWeek, after: PlansByWeek): PlacementChanges {
  const upserts: PlacementRow[] = [];
  const deletes: MealRef[] = [];
  for (const ws of new Set([...Object.keys(before), ...Object.keys(after)])) {
    if (before[ws] === after[ws]) continue;
    const old = rowsOf(ws, before[ws]);
    const now = rowsOf(ws, after[ws]);
    for (const [key, row] of now) {
      const was = old.get(key);
      if (!was || was.recipeId !== row.recipeId || was.isLeftover !== row.isLeftover || !sameSource(was.source, row.source) || was.cookedAt !== row.cookedAt) upserts.push(row);
    }
    for (const [key, row] of old) if (!now.has(key)) deletes.push({ weekStart: row.weekStart, day: row.day, mealSlot: row.mealSlot, dishType: row.dishType });
  }
  return { upserts, deletes };
}
